import { forwardRef } from "react";
import { cn } from "@/nucleo/utilitarios";

type SpinnerSize = "sm" | "md" | "lg";

const sizeMap: Record<SpinnerSize, { wrapper: string; spinner: string; border: string }> = {
  sm: { wrapper: "py-6", spinner: "h-5 w-5", border: "border-2" },
  md: { wrapper: "py-12", spinner: "h-6 w-6", border: "border-2" },
  lg: { wrapper: "py-16", spinner: "h-8 w-8", border: "border-[3px]" },
};

interface SpinnerProps {
  size?: SpinnerSize;
  className?: string;
  /** If true, uses min-h-dvh centering (for full-page loading) */
  fullPage?: boolean;
  /**
   * O que está carregando, anunciado por leitor de tela. A frase aparece só
   * para tecnologia assistiva; na tela continua sendo o disco girando.
   *
   * Passar o rótulo é o que transforma o disco em região viva. Sem ele o
   * componente é só desenho, e quem anuncia é a tela — várias já fazem isso,
   * com `role="status"` próprio e texto ao lado do disco. Anunciar nos dois
   * lugares daria duas regiões vivas aninhadas e a mesma frase duas vezes.
   */
  rotulo?: string;
}

/**
 * Indicador de carregamento.
 *
 * Com `rotulo`, emite `role="status"` e `aria-live="polite"` com o texto
 * só-para-leitor: antes daqui o componente devolvia dois `div` sem papel, sem
 * rótulo e sem texto, e a tela que não imprimia nada ficava muda entre o
 * clique e o conteúdo.
 */
export const Spinner = forwardRef<HTMLDivElement, SpinnerProps>(
  ({ size = "md", className, fullPage, rotulo }, ref) => {
    const s = sizeMap[size];
    const anuncia = Boolean(rotulo);
    return (
      <div
        ref={ref}
        role={anuncia ? "status" : undefined}
        aria-live={anuncia ? "polite" : undefined}
        aria-hidden={anuncia ? undefined : true}
        className={cn("flex items-center justify-center", fullPage ? "min-h-dvh" : s.wrapper, className)}
      >
        <div
          className={cn("animate-spin rounded-full border-primary/30 border-t-primary", s.spinner, s.border)}
        />
        {rotulo && <span className="sr-only">{rotulo}</span>}
      </div>
    );
  }
);
Spinner.displayName = "Spinner";
